import classNames from 'classnames';
import React, { ComponentProps, memo, TransitionEvent, useCallback, useRef, useState } from 'react';
import useUpdateEffect from '../../hooks/use-update-effect';
import styles from './collapse.module.sass';

interface CollapseProps extends ComponentProps<'div'> {
  open?: boolean;
}

/**
 * KU
 *
 * Collapsing component adapted from [here](
 * https://css-tricks.com/using-css-transitions-auto-dimensions/#aa-technique-3-javascript).
 *
 * I REALLY wanted to use a [CSS-only
 * approach](https://keithjgrant.com/posts/2023/04/transitioning-to-height-auto/#with-grid),
 * but it doesn't work in Safari <16 or Chrome <100
 */
export const Collapse = memo(({ open, ...props }: CollapseProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [height, setHeight] = useState<string | undefined>(open ? '' : '0px');

  useUpdateEffect(() => {
    window.requestAnimationFrame(() => {
      setHeight(`${ref.current?.scrollHeight}px`);
      if (!open) {
        window.setTimeout(() => {
          window.requestAnimationFrame(() => setHeight('0px'));
        });
      }
    });
  }, [open]);

  const handleTransitionEnd = useCallback(
    (e: TransitionEvent<HTMLDivElement>) => {
      if (e.currentTarget === ref.current && open) {
        window.requestAnimationFrame(() => {
          setHeight('');
        });
      }
    },
    [open],
  );

  return (
    <div
      className={classNames(styles.collapse, { [styles.open]: open })}
      ref={ref}
      style={{ height }}
      onTransitionEnd={handleTransitionEnd}
    >
      <div {...props} />
    </div>
  );
});

Collapse.displayName = 'Collapse';
